/**
 * Ask K.I.T. Chat Component
 * AI-powered trading journal assistant
 * 
 * Floating button + chat modal for asking questions about your trades
 */

import { useState, useRef, useEffect } from 'react'
import { apiFetch } from '../lib/api'

// ============================================================================
// TYPES
// ============================================================================

interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
}

interface AskKitChatProps {
  accountId?: string
  userId?: string
}

// ============================================================================
// GLASSMORPHISM COMPONENTS
// ============================================================================

const GlassCard = ({ children, className = '', glow = false }: { 
  children: React.ReactNode, 
  className?: string, 
  glow?: boolean 
}) => (
  <div className={`
    relative backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl
    ${glow ? 'shadow-[0_0_50px_rgba(6,182,212,0.15)]' : 'shadow-xl'}
    transition-all duration-300
    ${className}
  `}>
    {children}
  </div>
)

const GradientText = ({ children, className = '' }: { children: React.ReactNode, className?: string }) => (
  <span className={`bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 bg-clip-text text-transparent ${className}`}>
    {children}
  </span>
)

// ============================================================================
// QUICK PROMPTS
// ============================================================================

const quickPrompts = [
  { icon: '📊', text: "What's my win rate this week?" },
  { icon: '🎯', text: "Which setup performs best?" },
  { icon: '⚠️', text: "What mistakes am I making?" },
  { icon: '💹', text: "Analyze my EUR/USD trades" },
  { icon: '📝', text: "Give me feedback on my last 10 trades" },
  { icon: '🔥', text: "What's my best trading day?" },
]

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AskKitChat({ accountId, userId }: AskKitChatProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])

  // Send message to API
  const sendMessage = async (content: string) => {
    if (!content.trim() || isLoading) return

    const userMessage: ChatMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    }

    setMessages(prev => [...prev, userMessage])
    setInput('')
    setIsLoading(true)
    setError(null)

    try {
      const response = await apiFetch('/api/journal/ask-kit', {
        method: 'POST',
        body: JSON.stringify({
          message: content.trim(),
          accountId,
          conversationHistory: messages.slice(-10).map(m => ({
            role: m.role,
            content: m.content,
          })),
        }),
      })

      if (response.success && response.response) {
        const assistantMessage: ChatMessage = {
          id: `assistant_${Date.now()}`,
          role: 'assistant',
          content: response.response,
          timestamp: new Date(),
        }
        setMessages(prev => [...prev, assistantMessage])
      } else {
        setError(response.error || 'Failed to get response from K.I.T.')
      }
    } catch (err: any) {
      console.error('Ask K.I.T. error:', err)
      setError(err.message || 'Failed to connect to K.I.T.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    sendMessage(input)
  }

  const handleQuickPrompt = (prompt: string) => {
    sendMessage(prompt)
  }

  // Format message content with markdown-like styling
  const formatMessage = (content: string) => {
    // Simple formatting: bold, bullet points, numbers
    return content
      .split('\n')
      .map((line, i) => {
        // Bold text
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-cyan-400">$1</strong>')
        // Bullet points
        if (line.startsWith('• ') || line.startsWith('- ')) {
          return `<div class="flex gap-2 ml-2"><span class="text-cyan-400">•</span><span>${line.slice(2)}</span></div>`
        }
        // Numbered lists
        if (/^\d+\.\s/.test(line)) {
          const [num, ...rest] = line.split('. ')
          return `<div class="flex gap-2 ml-2"><span class="text-cyan-400 font-bold">${num}.</span><span>${rest.join('. ')}</span></div>`
        }
        return line ? `<p>${line}</p>` : '<br/>'
      })
      .join('')
  }

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`
          fixed bottom-6 right-6 z-40
          w-16 h-16 rounded-full
          bg-gradient-to-r from-cyan-500 to-blue-600
          shadow-[0_0_30px_rgba(6,182,212,0.5)]
          hover:shadow-[0_0_50px_rgba(6,182,212,0.7)]
          hover:scale-110 active:scale-95
          transition-all duration-300
          flex items-center justify-center
          group
        `}
        title="Ask K.I.T."
      >
        <span className="text-3xl group-hover:animate-bounce">🤖</span>
        {/* Pulse animation */}
        <span className="absolute inset-0 rounded-full bg-cyan-500/30 animate-ping" />
      </button>

      {/* Chat Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <GlassCard 
            className="w-full sm:w-[500px] h-[85vh] sm:h-[600px] max-h-[700px] flex flex-col sm:rounded-2xl rounded-t-2xl rounded-b-none" 
            glow
          >
            {/* Header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center">
                  <span className="text-xl">🤖</span>
                </div>
                <div>
                  <h2 className="font-bold text-white">Ask <GradientText>K.I.T.</GradientText></h2>
                  <p className="text-xs text-gray-400">Your AI Trading Journal Assistant</p>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-all"
              >
                ✕
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="text-6xl mb-4">🤖</div>
                  <h3 className="text-white font-semibold text-lg mb-2">
                    Hi! I'm <GradientText>K.I.T.</GradientText>
                  </h3>
                  <p className="text-gray-400 text-sm mb-6 max-w-xs">
                    Ask me anything about your trading journal. I can analyze your trades, 
                    find patterns, and help you improve!
                  </p>
                  
                  {/* Quick Prompts */}
                  <div className="grid grid-cols-2 gap-2 w-full max-w-sm">
                    {quickPrompts.map((prompt, i) => (
                      <button
                        key={i}
                        onClick={() => handleQuickPrompt(prompt.text)}
                        className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10
                                   hover:bg-cyan-500/10 hover:border-cyan-500/30 text-left text-sm text-gray-300
                                   transition-all duration-200 group"
                      >
                        <span className="text-lg group-hover:scale-110 transition-transform">{prompt.icon}</span>
                        <span className="truncate">{prompt.text}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`
                        max-w-[85%] rounded-2xl px-4 py-3
                        ${message.role === 'user'
                          ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 text-white'
                          : 'bg-white/5 border border-white/10 text-gray-200'
                        }
                      `}
                    >
                      {message.role === 'assistant' && (
                        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/10">
                          <span className="text-lg">🤖</span>
                          <span className="text-xs font-semibold text-cyan-400">K.I.T.</span>
                        </div>
                      )}
                      <div 
                        className="text-sm leading-relaxed space-y-1"
                        dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }}
                      />
                      <div className="text-[10px] text-gray-500 mt-2 text-right">
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))
              )}
              
              {/* Loading indicator */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg animate-bounce">🤖</span>
                      <span className="text-sm text-gray-400">K.I.T. is thinking</span>
                      <span className="flex gap-1">
                        <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Error message */}
              {error && (
                <div className="flex justify-center">
                  <div className="bg-rose-500/10 border border-rose-500/30 text-rose-400 rounded-xl px-4 py-2 text-sm">
                    ⚠️ {error}
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-4 border-t border-white/10 shrink-0">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about your trades..."
                  disabled={isLoading}
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white 
                             placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 
                             focus:ring-2 focus:ring-cyan-500/20 transition-all disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isLoading}
                  className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-medium
                             hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-all 
                             disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <span className="animate-spin">⏳</span>
                  ) : (
                    '→'
                  )}
                </button>
              </div>
              <p className="text-[10px] text-gray-500 mt-2 text-center">
                K.I.T. analyzes your journal data to provide personalized insights
              </p>
            </form>
          </GlassCard>
        </div>
      )}
    </>
  )
}
